import type { IntlShape } from 'react-intl';

import {
  API_DOMAIN_CODES,
  getCallableDomainCode,
  getCallableErrorCode,
} from '@src/util/cloud-functions/callableDomainCode';

const DEFAULT_MESSAGE_ID = 'users.invitation.accept.badToken';
const INVALID_DATA_MESSAGE_ID = 'users.invitation.accept.invalidData';

const DOMAIN_CODE_MESSAGE_IDS: Record<string, string> = {
  [API_DOMAIN_CODES.NATIONAL_ID_ALREADY_REGISTERED]:
    'users.invitation.accept.idCardAlreadyRegistered',
  [API_DOMAIN_CODES.ONBOARDING_ALREADY_COMPLETED]: 'users.invitation.accept.alreadyActive',
};

/** True when the invite is for an existing account, which must sign in to accept it. */
export const isInviteRequiresSignInError = (error: unknown): boolean =>
  getCallableDomainCode(error) === API_DOMAIN_CODES.INVITE_REQUIRES_SIGN_IN;

/** True when the invitee already finished onboarding (old link, second tab, etc.). */
export const isOnboardingAlreadyCompletedError = (error: unknown): boolean =>
  getCallableDomainCode(error) === API_DOMAIN_CODES.ONBOARDING_ALREADY_COMPLETED;

/**
 * Locale-aware copy for invite acceptance failures. The auth layer replaces the
 * backend message with generic copy, so the backend domain code (or callable
 * code) is what tells the invitee what to fix.
 */
export const resolveAcceptInvitationErrorMessage = (
  intl: IntlShape,
  error: unknown,
): string => {
  const domainCode = getCallableDomainCode(error);
  const messageId = domainCode ? DOMAIN_CODE_MESSAGE_IDS[domainCode] : undefined;

  if (messageId) {
    return intl.formatMessage({ id: messageId });
  }

  if (getCallableErrorCode(error) === 'functions/invalid-argument') {
    return intl.formatMessage({ id: INVALID_DATA_MESSAGE_ID });
  }

  return intl.formatMessage({ id: DEFAULT_MESSAGE_ID });
};
